import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Spinner } from "../../components/common/Spinner";
import { Card } from "../../components/common/Card";
import { MapPin, Phone, ArrowLeft, Copy, Clock } from "lucide-react";
import { toast } from "react-hot-toast";
import { OrderModificationModal } from "../../components/order/OrderModificationModal";
import { TRACK_STEPS, trackStep, kitchenStatusLabel, overallStatus, showDeliveryOtp } from "../../lib/orderTracking";

/**
 * Track order. The route id is normally the parent_orders id (WhatsApp links, push, orders list);
 * a kitchen-order id also works and is redirected to its parent. Everything is realtime.
 */
const OrderDetails: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [parent, setParent] = useState<any | null>(null);
  const [parentChecked, setParentChecked] = useState(false);
  const [kitchens, setKitchens] = useState<any[]>([]);
  const [kitchensLoaded, setKitchensLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modifyOrder, setModifyOrder] = useState<any | null>(null);

  useEffect(() => {
    if (!orderId) return;
    setParent(null); setParentChecked(false); setKitchens([]); setKitchensLoaded(false); setError(null);
    const onErr = (e: any) => {
      console.error("Track order listener", e);
      setError(e?.code === "permission-denied" ? "Please log in to track this order." : "Couldn't load this order. Pull to refresh.");
      setParentChecked(true); setKitchensLoaded(true);
    };
    const unsubParent = onSnapshot(doc(db, "parent_orders", orderId), (snap) => {
      setParent(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setParentChecked(true);
    }, onErr);
    const unsubKids = onSnapshot(query(collection(db, "orders"), where("parentOrderId", "==", orderId)), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() } as any));
      list.sort((a, b) => String(a.kitchenName || "").localeCompare(String(b.kitchenName || "")));
      if (list.length) { setKitchens(list); setKitchensLoaded(true); }
      else setKitchensLoaded((prev) => prev || false);
    }, onErr);
    // Legacy / kitchen-order id: load it directly; redirect to its parent when it has one.
    const unsubSelf = onSnapshot(doc(db, "orders", orderId), (snap) => {
      if (snap.exists()) {
        const o = { id: snap.id, ...snap.data() } as any;
        if (o.parentOrderId && o.parentOrderId !== orderId) {
          navigate(`/orders/${encodeURIComponent(o.parentOrderId)}`, { replace: true });
          return;
        }
        setKitchens((prev) => (prev.length && prev[0].parentOrderId === orderId ? prev : [o]));
      }
      setKitchensLoaded(true);
    }, () => setKitchensLoaded(true));
    return () => { unsubParent(); unsubKids(); unsubSelf(); };
  }, [orderId, navigate]);

  const loading = !parentChecked || (!kitchensLoaded && !error);
  if (loading) return <div className="p-10 flex justify-center"><Spinner /></div>;
  if (error) return <div className="p-10 text-center text-red-600">{error} <div className="mt-3"><Link className="text-orange-600 underline" to="/orders">Your orders</Link></div></div>;
  if (!parent && kitchens.length === 0) return <div className="p-10 text-center">Order not found <div className="mt-3"><Link className="text-orange-600 underline" to="/orders">Your orders</Link></div></div>;

  const head = parent || kitchens[0];
  const multi = kitchens.length > 1;
  const status = kitchens.length ? overallStatus(kitchens.map((k) => String(k.status || ""))) : String(head.status || "pending");
  const statusColor = status === "delivered" ? "text-green-600" : status === "cancelled" ? "text-red-600" : "text-orange-600";
  const displayId = String(head.id).slice(0, 6).toUpperCase();

  const formatDate = (ts: any) => {
    if (!ts) return "";
    try {
      const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
      return isNaN(d.getTime()) ? "" : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
  };
  const copyOtp = (otp: string) => { navigator.clipboard?.writeText(otp); toast.success("Code copied!"); };
  const address = typeof head.shippingAddress === "string" && head.shippingAddress
    ? head.shippingAddress
    : head.deliveryAddress?.fullAddress || head.deliveryAddress?.address || (typeof head.deliveryAddress === "string" ? head.deliveryAddress : "");

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-20">
      <div className="max-w-lg mx-auto space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-200 rounded-full" aria-label="Back">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Track order</h1>
        </div>

        <Card className="p-5">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs text-gray-400 uppercase font-bold tracking-wide">{multi ? `Overall (${kitchens.length} kitchens)` : "Status"}</div>
              <div className={`text-2xl font-bold capitalize ${statusColor}`} data-testid="overall-status">
                {multi || !kitchens[0] ? status.replace(/_/g, " ") : kitchenStatusLabel(kitchens[0])}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500 mt-1"><Clock size={12} /> {formatDate(head.createdAt)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-400 uppercase font-bold">Order</div>
              <div className="font-mono text-gray-600">#{displayId}</div>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">Updates live. No need to refresh.</p>
        </Card>

        {kitchens.map((k) => {
          const step = trackStep(String(k.status || ""));
          const cancelled = step < 0;
          return (
            <Card key={k.id} className="p-5" data-testid="kitchen-track">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-bold text-gray-800">{k.kitchenName || "Kitchen"}</h3>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${cancelled ? "bg-red-100 text-red-700" : step >= 4 ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                  {kitchenStatusLabel(k)}
                </span>
              </div>
              {!cancelled && (
                <ol className="flex items-center justify-between gap-1 mb-3" aria-label="Progress">
                  {TRACK_STEPS.map((label, i) => (
                    <li key={label} className="flex-1 flex flex-col items-center text-center">
                      <div className={`w-full h-1.5 rounded-full ${i <= step ? "bg-orange-500" : "bg-gray-200"}`} />
                      <span className={`mt-1 text-[10px] leading-tight ${i === step ? "text-orange-700 font-bold" : i < step ? "text-gray-600" : "text-gray-400"}`}>{label}</span>
                    </li>
                  ))}
                </ol>
              )}
              {k.status === "out_for_delivery" && k.selfDelivery && (
                <p className="text-sm text-gray-600 bg-orange-50 rounded p-2 mb-3">The kitchen is bringing this part of your order personally.</p>
              )}
              {showDeliveryOtp(k) && (
                <button type="button" onClick={() => copyOtp(k.deliveryOtp)} className="w-full bg-orange-50 border-2 border-dashed border-orange-300 rounded-xl p-4 text-center mb-3">
                  <span className="text-xs text-orange-600 uppercase font-bold flex justify-center items-center gap-1">Share code with delivery partner <Copy size={12} /></span>
                  <span className="block text-4xl font-black text-gray-800">{k.deliveryOtp}</span>
                </button>
              )}
              <div className="space-y-2">
                {(k.items || []).map((item: any, idx: number) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span><span className="font-bold text-gray-600">{item.quantity}x</span> {item.name}</span>
                    <span className="font-mono">₹{(item.price || 0) * (item.quantity || 1)}</span>
                  </div>
                ))}
              </div>
              {k.status === "delivered" && k.deliveredAt && <p className="text-xs text-green-700 mt-2">Delivered {formatDate(k.deliveredAt)}</p>}
              {(k.status === "pending" || k.status === "confirmed") && (
                <button onClick={() => setModifyOrder(k)} className="mt-3 w-full bg-orange-500 text-white font-bold py-2 rounded-xl">✏️ Modify</button>
              )}
            </Card>
          );
        })}

        <Card className="p-5 space-y-3">
          <div className="flex justify-between text-sm text-gray-500"><span>Delivery fee</span><span>₹{head.deliveryFee || 0}</span></div>
          <div className="flex justify-between font-bold text-lg text-gray-900"><span>Total</span><span>₹{head.totalAmount}</span></div>
          {address && <div className="flex items-start gap-3"><MapPin className="text-gray-400 mt-0.5" size={18} /><div className="text-sm text-gray-600">{address}</div></div>}
          {head.customerPhone && <div className="flex items-center gap-3"><Phone className="text-gray-400" size={18} /><div className="text-sm text-gray-600">{head.customerPhone}</div></div>}
          {head.paymentMethod === "food" && <div className="text-orange-700 bg-orange-50 p-2 rounded text-sm font-bold text-center">💵 Pay on delivery</div>}
          {head.paymentStatus === "paid" && <div className="text-green-700 bg-green-50 p-2 rounded text-sm font-bold text-center">✅ Paid</div>}
        </Card>

        {modifyOrder && (
          <OrderModificationModal
            isOpen
            onClose={() => setModifyOrder(null)}
            orderId={modifyOrder.id}
            userId={modifyOrder.customerId}
            originalItems={modifyOrder.items}
            originalTotal={modifyOrder.totalAmount}
            onSuccess={() => { setModifyOrder(null); toast.success("Order updated successfully!"); }}
          />
        )}
      </div>
    </div>
  );
};

export default OrderDetails;
